/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, JSX, ReactNode } from "react";

import { Log } from "../../../model/logging";
import { toGridPosition } from "../../../model/navigation/GridNavigation";
import {
  resolveMetrics,
  resolveRenderedItemCount,
  resolveRowOffset,
  resolveRowWindow,
  resolveScrollRow,
  resolveViewportHeight,
  rowCountFor
} from "./ContentGridGeometry";
import type { TResolvedMetrics, TRowWindow } from "./ContentGridGeometry";
import {
  ContentGridSkeletonCells,
  ContentGridSkeletonRow,
  resolveSkeletonRowCount
} from "./ContentGridSkeleton";
import type {
  ContentGridItemContext,
  ContentGridProps
} from "./ContentGridProps";

import "./ContentGrid.scss";

const TAG: string = "ContentGrid";

/** Rows mounted above and below the visible window when not overridden. */
const DEFAULT_OVERSCAN_ROWS: number = 1;

/** Distance from the last row at which `onNearEnd` fires when not overridden. */
const DEFAULT_NEAR_END_THRESHOLD_ROWS: number = 2;

/**
 * Screen-agnostic grid.
 *
 * Owns layout, focus geometry, row windowing and scrolling. It owns neither the
 * cell content (supplied via `renderItem`) nor the focus state (supplied via
 * `focusedIndex`, moved by the screen's `ScreenController` using
 * `moveGridFocus` from `model/navigation`) — which is what lets one component
 * back a poster wall, a channel picker and a recordings list unchanged.
 *
 * It also owns no interactivity: cell wrappers are presentational, and the
 * `onFocus`/`onSelect` callbacks are handed to `renderItem` pre-bound so the
 * rendered item — which is already the focusable, keyboard-capable element —
 * remains the single interactive layer.
 *
 * Loading is owned here as well, and for the same reason layout is: the
 * placeholders are laid out by the geometry above, so a loading grid and a
 * loaded grid cannot disagree about cell size, gap or column count.
 *
 * - **First load** (`isLoading`, no items) — a wall of placeholder cells
 *   filling the viewport. Note that this takes precedence over `emptyState`:
 *   an empty grid that is still fetching is not an empty grid, and flashing
 *   "nothing here" before the first page lands is the bug this prevents.
 * - **Refresh** (`isLoading` over items that already arrived) — the wall stays
 *   and dims, so nothing is taken away from a user who is already reading it.
 * - **Paging** (`isLoadingMore`) — placeholders complete the final partial row
 *   and add one row below it, which is exactly where the incoming page will
 *   appear. Real indices are untouched, so focus does not move.
 *
 * SmartTV notes:
 * - Scrolling is a single `translateY` on the track — no layout-triggering
 *   properties are animated.
 * - Only rows inside the window are mounted, so a 500-item grid costs the same
 *   as a 20-item one.
 */
const ContentGridComponent = <TItem,>(
  props: ContentGridProps<TItem>
): JSX.Element => {
  const {
    items,
    columns,
    renderItem,
    metrics,
    getItemKey,
    visibleRows,
    maxRows,
    focusedIndex,
    isActive,
    onItemFocus,
    onItemSelect,
    overscanRows,
    onNearEnd,
    nearEndThresholdRows,
    className,
    ariaLabel,
    emptyState,
    isLoading,
    isLoadingMore,
    skeletonRows,
    renderSkeletonItem
  } = props;

  // ── Defaults ──
  const resolvedFocusedIndex: number =
    focusedIndex === undefined ? -1 : focusedIndex;
  const isGridActive: boolean = isActive === undefined ? true : isActive;
  const resolvedOverscan: number =
    overscanRows === undefined ? DEFAULT_OVERSCAN_ROWS : overscanRows;
  // 0 means "no internal scroller — lay out every row".
  const resolvedVisibleRows: number = Math.max(visibleRows || 0, 0);

  // ── Geometry ──
  const renderedItemCount: number = resolveRenderedItemCount(
    items.length,
    columns,
    maxRows
  );
  const rowCount: number = rowCountFor(renderedItemCount, columns);
  const focusedRow: number = toGridPosition(
    Math.max(resolvedFocusedIndex, 0),
    columns
  ).row;

  /**
   * Scroll offset is derived from focus but remembers where it was, so a focus
   * move inside the visible window does not scroll at all.
   *
   * Stored as state adjusted during render (React's documented
   * derive-state-from-props pattern) rather than in an effect, so the offset is
   * correct on the very first paint — an effect would show one unscrolled
   * frame on every entry. `resolveScrollRow` is idempotent when fed its own
   * output, so this settles in a single extra render and cannot loop.
   */
  const [storedScrollRow, setStoredScrollRow] = useState<number>(0);
  const scrollRow: number = resolveScrollRow(
    focusedRow,
    storedScrollRow,
    resolvedVisibleRows,
    rowCount
  );
  if (scrollRow !== storedScrollRow) {
    setStoredScrollRow(scrollRow);
  }

  const rowWindow: TRowWindow = resolveRowWindow(
    scrollRow,
    resolvedVisibleRows,
    resolvedOverscan,
    rowCount
  );

  const resolvedMetrics: TResolvedMetrics = useMemo(
    (): TResolvedMetrics => resolveMetrics(metrics),
    [metrics]
  );

  // ── Invalid configuration is a developer error, not a crash ──
  useEffect((): void => {
    if (columns < 1) {
      Log.w(
        TAG,
        "ContentGrid() received columns=" + columns + " — nothing will render"
      );
    }
  }, [columns]);

  // ── Pagination hook: fire once per threshold crossing ──
  const hasFiredNearEndRef = useRef<boolean>(false);
  useEffect((): void => {
    if (onNearEnd === undefined || rowCount === 0) {
      return;
    }
    const threshold: number =
      nearEndThresholdRows === undefined
        ? DEFAULT_NEAR_END_THRESHOLD_ROWS
        : nearEndThresholdRows;
    const isNearEnd: boolean = focusedRow >= rowCount - threshold;
    if (isNearEnd && !hasFiredNearEndRef.current) {
      hasFiredNearEndRef.current = true;
      onNearEnd();
    } else if (!isNearEnd) {
      hasFiredNearEndRef.current = false;
    }
  }, [focusedRow, rowCount, nearEndThresholdRows, onNearEnd]);

  // Growing the data set re-arms the trigger for the next page.
  useEffect((): void => {
    hasFiredNearEndRef.current = false;
  }, [renderedItemCount]);

  // ── Loading ──
  // The three states are mutually exclusive by construction: whether any item
  // has arrived decides between standing in for the wall and merely dimming it,
  // and only a grid that has content can have more appended to it.
  const hasItems: boolean = renderedItemCount > 0;
  const isBusy: boolean = isLoading === true || isLoadingMore === true;
  const isFirstLoad: boolean = isLoading === true && !hasItems && columns >= 1;
  const isRefreshing: boolean = isLoading === true && hasItems;
  const isAppending: boolean =
    isLoadingMore === true && isLoading !== true && hasItems;

  // CSS custom properties, not visual styling: every dynamic length lives here
  // so the stylesheet owns all of the actual layout rules. Lengths are already
  // multiplied out, so the stylesheet needs no `calc()` and no operators.
  //
  // Computed before any early return so the placeholder wall is sized by the
  // very same values the real wall is.
  const rootStyle: CSSProperties = {
    "--content-grid-item-width": resolvedMetrics.itemWidth,
    "--content-grid-item-height": resolvedMetrics.itemHeight,
    "--content-grid-content-width": resolvedMetrics.contentWidth,
    "--content-grid-content-height": resolvedMetrics.contentHeight,
    "--content-grid-column-gap": resolvedMetrics.columnGap,
    "--content-grid-row-gap": resolvedMetrics.rowGap,
    "--content-grid-viewport-height": resolveViewportHeight(
      resolvedMetrics,
      resolvedVisibleRows
    )
  } as CSSProperties;

  const rootClassName = (modifiers: string[]): string =>
    ["content-grid", ...modifiers, className]
      .filter(
        (token?: string): token is string => token !== undefined && token !== ""
      )
      .join(" ");

  if (isFirstLoad) {
    const skeletonRowCount: number = resolveSkeletonRowCount(
      skeletonRows,
      resolvedVisibleRows,
      maxRows
    );

    return (
      <section
        className={rootClassName([
          resolvedVisibleRows > 0 ? "content-grid--scrollable" : "",
          "content-grid--loading"
        ])}
        style={rootStyle}
        aria-label={ariaLabel}
        aria-busy={true}
      >
        <div className="content-grid__viewport">
          <div className="content-grid__track">
            <div className="content-grid__window">
              {Array.from(
                { length: skeletonRowCount },
                (_: unknown, row: number): JSX.Element => (
                  <ContentGridSkeletonRow
                    key={"skeleton-row-" + row}
                    row={row}
                    count={columns}
                    startIndex={row * columns}
                    renderItem={renderSkeletonItem}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0 || columns < 1) {
    return (
      <section
        className={rootClassName(["content-grid--empty"])}
        aria-label={ariaLabel}
        aria-busy={isBusy}
      >
        {emptyState}
      </section>
    );
  }

  const trackStyle: CSSProperties = {
    transform:
      "translateY(-" + resolveRowOffset(resolvedMetrics, scrollRow) + ")"
  };

  // Reserves the space of the rows above the window that are not mounted.
  const windowStyle: CSSProperties = {
    marginTop: resolveRowOffset(resolvedMetrics, rowWindow.startRow)
  };

  /** Last row that holds real data — where a pending page attaches. */
  const lastRow: number = rowCount - 1;

  const renderRow = (row: number): JSX.Element => {
    const firstIndex: number = row * columns;
    const cells: JSX.Element[] = [];

    for (let column: number = 0; column < columns; column++) {
      const index: number = firstIndex + column;
      if (index >= renderedItemCount) {
        break;
      }
      const item: TItem | undefined = items[index];
      if (item === undefined) {
        break;
      }
      const isFocused: boolean = isGridActive && index === resolvedFocusedIndex;
      const context: ContentGridItemContext<TItem> = {
        item,
        index,
        row,
        column,
        isFocused,
        onFocus: (): void => {
          if (onItemFocus !== undefined) {
            onItemFocus(index);
          }
        },
        onSelect: (): void => {
          if (onItemSelect !== undefined) {
            onItemSelect(index);
          }
        }
      };
      const key: string =
        getItemKey === undefined ? String(index) : getItemKey(item, index);

      // Presentational wrapper only: it sizes and positions the cell. Roles,
      // tab stops and handlers belong to the item the caller renders inside —
      // wrapping an already-interactive item (Tile is a focusable
      // role="button") in a second clickable layer doubles every event and
      // produces a tab stop that the D-pad focus model does not control.
      cells.push(
        <div
          key={key}
          className={
            "content-grid__cell" +
            (isFocused ? " content-grid__cell--focused" : "")
          }
          data-index={index}
        >
          {renderItem(context)}
        </div>
      );
    }

    // While a further page is in flight, the final row is completed with
    // placeholders. That is where the incoming items will actually land, so the
    // gap the user sees is the gap that is about to be filled — and on the
    // clipped variant it is the only part of the pending page that is on screen
    // at all.
    if (isAppending && row === lastRow && cells.length < columns) {
      cells.push(
        <ContentGridSkeletonCells
          key={"skeleton-fill-" + row}
          count={columns - cells.length}
          startIndex={firstIndex + cells.length}
          renderItem={renderSkeletonItem}
        />
      );
    }

    // Rows exist for layout and windowing, not semantics — the grid is not
    // tabular data, so it claims no table/grid roles.
    return (
      <div className="content-grid__row" key={"row-" + row}>
        {cells}
      </div>
    );
  };

  const rows: ReactNode[] = [];
  for (let row: number = rowWindow.startRow; row <= rowWindow.endRow; row++) {
    rows.push(renderRow(row));
  }

  // One further row of placeholders, but only once the mounted window has
  // reached the end of the data — anywhere earlier it would be drawn into rows
  // the user is nowhere near, and windowing exists precisely to avoid that.
  if (isAppending && rowWindow.endRow === lastRow) {
    rows.push(
      <ContentGridSkeletonRow
        key={"skeleton-row-" + rowCount}
        row={rowCount}
        count={columns}
        startIndex={rowCount * columns}
        renderItem={renderSkeletonItem}
      />
    );
  }

  return (
    <section
      className={rootClassName([
        resolvedVisibleRows > 0 ? "content-grid--scrollable" : "",
        isGridActive ? "content-grid--active" : "",
        isRefreshing ? "content-grid--refreshing" : ""
      ])}
      style={rootStyle}
      aria-label={ariaLabel}
      aria-busy={isBusy}
      data-row-count={rowCount}
      data-column-count={columns}
    >
      <div className="content-grid__viewport">
        <div className="content-grid__track" style={trackStyle}>
          <div
            className="content-grid__window"
            style={windowStyle}
            data-start-row={rowWindow.startRow}
            data-end-row={rowWindow.endRow}
          >
            {rows}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Memoized to keep unrelated screen re-renders (clock ticks, playback state)
 * off the grid. Default shallow comparison, so callbacks and `metrics` should
 * be stable references (`useCallback` / `useMemo` / module constant) at the
 * call site.
 *
 * `memo` erases the generic parameter, so the cast restores the generic call
 * signature — this is why the component is not typed as `FunctionComponent`.
 */
const ContentGrid = memo(ContentGridComponent) as <TItem>(
  props: ContentGridProps<TItem>
) => JSX.Element;

export default ContentGrid;
